import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FlaskConical, Quote, Star } from "lucide-react";
import { useState } from "react";

import type { ContentItem } from "@/content/types";
import { trackClick } from "@/lib/analytics-client";
import { pathFor, type Locale } from "@/lib/i18n";
import { listPublishedTestimonials } from "@/lib/testimonials.functions";
import { useTestimonialsDisplay } from "@/lib/testimonials-display";
import { displayName, type Testimonial } from "@/lib/testimonials";

const TEST_BADGE: Record<Locale, string> = {
  sv: "TEST – demoexempel",
  en: "TEST – demo example",
  es: "TEST – ejemplo de demostración",
};

const COPY: Record<Locale, { featured: string; readAll: string; readMore: string; readLess: string; source: string }> = {
  sv: {
    featured: "Utvalt omdöme",
    readAll: "Läs fler omdömen",
    readMore: "Läs hela omdömet",
    readLess: "Visa mindre",
    source: "Källa",
  },
  en: {
    featured: "Featured review",
    readAll: "Read more reviews",
    readMore: "Read the full review",
    readLess: "Show less",
    source: "Source",
  },
  es: {
    featured: "Opinión destacada",
    readAll: "Leer más opiniones",
    readMore: "Leer la opinión completa",
    readLess: "Ver menos",
    source: "Fuente",
  },
};

const LOCALE_TAG: Record<Locale, string> = { sv: "sv-SE", en: "en-GB", es: "es-ES" };

/** Rutnätet ska se rätt ut även med 1, 2 eller 3 kort. */
export function gridClassFor(count: number): string {
  if (count <= 1) return "mx-auto grid max-w-2xl gap-5";
  if (count === 2) return "mx-auto grid max-w-4xl gap-5 sm:grid-cols-2";
  return "grid gap-5 sm:grid-cols-2 lg:grid-cols-3";
}

function formatDate(value: string | null | undefined, locale: Locale): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(LOCALE_TAG[locale], {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Europe/Madrid",
  });
}

/**
 * Kortar ett långt citat på närmaste meningsslut runt halva texten. Aldrig
 * mitt i ett ord, och hela originalet finns alltid kvar i DOM:en.
 */
function shortenQuote(quote: string, target: number): string | null {
  const text = quote.trim();
  if (text.length <= target * 1.3) return null;

  // Föredra ett styckebrott strax efter halva texten.
  const paragraphBreak = text.indexOf("\n\n", Math.round(target * 0.5));
  if (paragraphBreak > 0 && paragraphBreak < target * 1.4) return text.slice(0, paragraphBreak).trim();

  const window = text.slice(0, Math.round(target * 1.3));
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? "),
  );
  if (sentenceEnd > target * 0.4) return window.slice(0, sentenceEnd + 1).trim();

  const wordEnd = window.lastIndexOf(" ");
  return wordEnd > 0 ? window.slice(0, wordEnd).trim() : null;
}

/**
 * Citat med "Läs hela"-utfällning. Hela texten renderas alltid – den korta
 * varianten döljs bara visuellt – så sökmotorer och skärmläsare ser allt.
 * Varje utfällning loggas så vi kan följa hur många som läser hela omdömet.
 */
function ExpandableQuote({
  review,
  locale,
  target,
  place,
  className,
  buttonClassName = "",
}: {
  review: Testimonial;
  locale: Locale;
  target: number;
  place: string;
  className: string;
  buttonClassName?: string;
}) {
  const copy = COPY[locale];
  const [open, setOpen] = useState(false);
  const short = shortenQuote(review.quote, target);

  const shown = !short || open ? review.quote : `${short} …`;

  return (
    <>
      <blockquote className={className}>
        “{shown}”
        {/* Hela originaltexten finns alltid i sidan, även när citatet är ihopfällt. */}
        {short && !open ? <span className="sr-only">{review.quote}</span> : null}
      </blockquote>
      {short ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next) {
              trackClick("testimonial_expand", {
                place,
                testimonial: review.id,
                name: displayName(review),
              });
            }
          }}
          className={`link-quiet mt-3 inline-flex min-h-11 w-fit items-center py-2 text-sm font-medium text-primary ${buttonClassName}`}
        >
          {open ? copy.readLess : copy.readMore}
        </button>
      ) : null}
    </>
  );
}


export function Stars({ rating, className = "size-4" }: { rating: number; className?: string }) {
  return (
    <div className="flex gap-0.5" role="img" aria-label={`${rating} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${className} ${n <= rating ? "fill-primary text-primary" : "text-border"}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function TestBadge({ locale }: { locale: Locale }) {
  return (
    <p className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-amber-500/60 bg-amber-500/10 px-2.5 py-1 text-[0.65rem] font-semibold tracking-[0.08em] text-amber-700 uppercase dark:text-amber-400">
      <FlaskConical aria-hidden className="size-3" />
      {TEST_BADGE[locale]}
    </p>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <figure className="flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-soft">
      {children}
    </figure>
  );
}

/** Ett omdöme som kort – används både på startsidan och omdömessidan. */
export function ReviewCard({ review, locale }: { review: Testimonial; locale: Locale }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        {review.imageUrl ? (
          <img
            src={review.imageUrl.includes("?") ? `${review.imageUrl}&w=160` : `${review.imageUrl}?w=160`}
            alt=""
            aria-hidden
            role="presentation"
            loading="lazy"
            decoding="async"
            width={48}
            height={48}
            className="size-12 rounded-full bg-muted object-cover"
          />
        ) : (
          <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            {(review.firstName || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
        <Stars rating={review.rating} />
      </div>
      <ExpandableQuote
        review={review}
        locale={locale}
        target={280}
        place="card"
        className="mt-4 text-sm leading-relaxed whitespace-pre-line text-foreground"
      />
      <figcaption className="mt-auto pt-4 text-xs font-medium text-muted-foreground">
        {displayName(review)}
        {review.roleLabel ? ` – ${review.roleLabel}` : ""}
        {formatDate(review.publishedAt, locale) ? ` · ${formatDate(review.publishedAt, locale)}` : ""}
      </figcaption>
      {review.isTest ? <TestBadge locale={locale} /> : null}
    </Card>
  );
}

/** Rutnät med omdömen, utan egen sektionsram. */
export function ReviewGrid({ reviews, locale }: { reviews: Testimonial[]; locale: Locale }) {
  return (
    <div className={gridClassFor(reviews.length)}>
      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} locale={locale} />
      ))}
    </div>
  );
}

/**
 * Utvalt omdöme på startsidan – det som ligger först i sorteringen i admin.
 * Långa citat kortas visuellt och kan fällas ut. Visas inte alls om det
 * saknas riktiga publicerade omdömen.
 */
function FeaturedTestimonial({ locale, review, publicPage }: { locale: Locale; review: Testimonial; publicPage: boolean }) {
  const copy = COPY[locale];

  const date = formatDate(review.publishedAt, locale);

  return (
    <section className="border-y border-border bg-sand">
      <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20">
        <p className="text-[0.7rem] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {copy.featured}
        </p>

        <figure className="mt-6">
          <Stars rating={review.rating} className="size-5" />
          {/* Ungefär halva citatet visas, resten fälls ut – originaltexten ändras aldrig. */}
          <ExpandableQuote
            review={review}
            locale={locale}
            target={Math.round(review.quote.length / 2)}
            place="start"
            className="mt-5 font-display text-xl leading-relaxed whitespace-pre-line text-foreground sm:text-2xl"
          />


          <figcaption className="mt-6 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{displayName(review)}</span>
            {review.roleLabel ? ` · ${review.roleLabel}` : ""}
            {date ? ` · ${date}` : ""}
            {review.source === "instagram" ? ` · ${copy.source}: Instagram` : ""}
          </figcaption>
        </figure>

        {publicPage ? (
          <Link
            to={pathFor("/omdomen", locale) as never}
            className="mt-8 inline-flex text-sm font-medium text-primary link-quiet"
          >
            {copy.readAll}
          </Link>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Röster från familjer. Visar publicerade omdömen som föräldrar själva har
 * godkänt, samt eventuella citat redaktionen lagt in i innehållsblocket.
 * Demo-omdömen (is_test) får alltid en tydlig TEST-badge.
 */
export function Testimonials({
  title,
  items,
  locale = "sv",
  initialReviews,
}: {
  title: string;
  items: ContentItem[];
  locale?: Locale;
  initialReviews?: Testimonial[];
}) {
  const { data } = useQuery({
    queryKey: ["testimonials", "published", "home", locale],
    queryFn: () => listPublishedTestimonials({ data: { limit: 12, locale, homeOnly: true } }),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
  });

  const settings = useTestimonialsDisplay();
  const selected = (data ?? initialReviews ?? []).slice(0, settings.homeLimit);
  const featured = selected.find((review) => !review.isTest);
  const reviews = selected.filter((review) => review.id !== featured?.id);
  // Innehållsblockets äldre citat får inte duplicera eller ersätta adminvalen.
  const fallbackItems: ContentItem[] = [];
  if (!selected.length) return null;

  return (
    <>
    {featured ? <FeaturedTestimonial locale={locale} review={featured} publicPage={settings.publicPage} /> : null}
    {reviews.length > 0 ? (
    <section id="roster" className="border-y border-border bg-sand">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20">
        <h2 className="text-2xl font-semibold sm:text-3xl">{title}</h2>
        <div className={`mt-8 ${gridClassFor(reviews.length + fallbackItems.length)}`}>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} locale={locale} />
          ))}

          {fallbackItems.map((item) => (
            <Card key={item.title}>
              <Quote className="size-5 text-primary" aria-hidden="true" />
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground">{item.text}</blockquote>
              <figcaption className="mt-auto pt-4 text-xs font-medium text-muted-foreground">
                {item.title}
              </figcaption>
            </Card>
          ))}
        </div>
        {settings.publicPage ? (
          <Link
            to={pathFor("/omdomen", locale) as never}
            className="mt-8 inline-flex text-sm font-medium text-primary link-quiet"
          >
            {COPY[locale].readAll}
          </Link>
        ) : null}
      </div>
    </section>
    ) : null}
    </>
  );
}
